import React from 'react';
import clsx from 'clsx';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  PatientAuthorizationUtilizationState,
} from 'dtos/patientAuthorizationUtilizationState';
import ToolTip from 'core/components/Tooltip';
import NotesIcon from 'core/icons/notes';
import type {
  ClaimAuthorizationOption,
} from 'views/EMR/components/Authorizations/utils';
import {
  getAuthorizationStatusLabel,
} from 'views/EMR/components/Authorizations/utils';
import AuthorizationNotes from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/components/AuthorizationNotes';
import {
  useHideUsedAuthorizationStatus,
} from 'pages/Dashboard/pages/Billing/ClaimForm/UsedAuthorizationsContext';

type MatchedAuthorizationIndicatorsProps = {
  option: ClaimAuthorizationOption;
  showMatchDot: boolean;
};

type MatchIndicatorTooltipContentProps = {
  option: ClaimAuthorizationOption;
  statusLabel: string;
  hasNotes: boolean;
};

const AUTHORIZATION_STATUS_DOT_CLASS_MAP: Record<
  PatientAuthorizationUtilizationState,
  string
> = {
  [PatientAuthorizationUtilizationState.Available]: 'bg-emerald-500',
  [PatientAuthorizationUtilizationState.Upcoming]: 'bg-blue-600',
  [PatientAuthorizationUtilizationState.Expired]: 'bg-gray-600',
  [PatientAuthorizationUtilizationState.Exhausted]: 'bg-red-600',
  [PatientAuthorizationUtilizationState.PartlyExhausted]: 'bg-amber-800',
};

function MatchIndicatorTooltipContent({
  option,
  statusLabel,
  hasNotes,
}: MatchIndicatorTooltipContentProps) {
  return (
    <div className="flex max-w-xs flex-col gap-2 text-xs">
      <div>
        <span className="font-semibold">Status:</span>
        {' '}
        {statusLabel || 'Unknown'}
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-semibold">Notes:</span>
        {hasNotes ? <AuthorizationNotes option={option} /> : <span>Not available</span>}
      </div>
    </div>
  );
}

export default function MatchedAuthorizationIndicators({
  option,
  showMatchDot,
}: MatchedAuthorizationIndicatorsProps) {
  const hasNotes = option.authorizations.some((authorization) => (
    notEmptyString(authorization.notes)
  ));
  const hideStatus = useHideUsedAuthorizationStatus(option);
  const { status } = option;
  const statusDotClassName = status
    ? AUTHORIZATION_STATUS_DOT_CLASS_MAP[status]
    : undefined;
  const statusLabel = status ? getAuthorizationStatusLabel(status) : '';

  return (
    <ToolTip
      title={(
        <MatchIndicatorTooltipContent
          option={option}
          statusLabel={statusLabel}
          hasNotes={hasNotes}
        />
      )}
      placement="right"
    >
      <span className="pointer-events-auto flex items-center gap-1 pr-1">
        {hasNotes && (
          <NotesIcon
            titleAccess="Has notes"
            className="!h-4 !w-4 shrink-0 text-blue-600"
          />
        )}
        {showMatchDot && !hideStatus && statusDotClassName && (
          <span
            aria-label={`Matches an existing ${statusLabel} authorization`}
            className={clsx('h-2 w-2 shrink-0 rounded-full', statusDotClassName)}
          />
        )}
      </span>
    </ToolTip>
  );
}
